import {useCallback, useEffect, useState} from 'react';
import {format} from 'date-fns';
import {Link, useSearchParams} from 'react-router-dom';
import {AlertCircle, Archive, ArrowRight, ExternalLink, FileBarChart, RefreshCw,} from 'lucide-react';

import {type ExerciseArchiveListItem, type ExerciseArchiveListResponse, v2Api} from '@/lib/api';
import {Button} from '@/components/ui/button';
import {Badge} from '@/components/ui/badge';
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert';
import {Skeleton} from '@/components/ui/skeleton';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from '@/components/ui/table';
import {Pagination} from '@/components/ui/Pagination';
import {Select} from '@/components/ui/select';
import BulkArchiveReportExport, {type SelectedArchiveItem} from '@/components/reports/BulkArchiveReportExport';
import {cn} from '@/lib/utils';

const DEFAULT_ARCHIVE_PAGE_SIZE = 10;
/** Must stay within backend `ExercisePageNumberPagination.max_page_size` (100). */
const ARCHIVE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
type DateFilterMode = 'all' | 'today' | 'yesterday' | 'custom';

function parsePageSizeParam(raw: string | null): number {
    const n = Number.parseInt(raw || '', 10);
    if (ARCHIVE_PAGE_SIZE_OPTIONS.includes(n as (typeof ARCHIVE_PAGE_SIZE_OPTIONS)[number])) {
        return n;
    }
    return DEFAULT_ARCHIVE_PAGE_SIZE;
}

function formatRunPeriod(start: string, end: string | null): string {
    try {
        const s = new Date(start);
        if (Number.isNaN(s.getTime())) return '—';
        const left = format(s, 'MMM d, yyyy · HH:mm');
        if (!end) return `${left} → —`;
        const e = new Date(end);
        if (Number.isNaN(e.getTime())) return left;
        return `${left} → ${format(e, 'MMM d, yyyy · HH:mm')}`;
    } catch {
        return '—';
    }
}

export default function ExerciseArchivesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const pageFromUrl = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
    const pageSize = parsePageSizeParam(searchParams.get('page_size'));
    const dateFilter = searchParams.get('date_filter');
    const archivedFrom = searchParams.get('archived_from') || '';
    const archivedTo = searchParams.get('archived_to') || '';
    const initialDateMode: DateFilterMode =
        dateFilter === 'today'
            ? 'today'
            : dateFilter === 'yesterday'
                ? 'yesterday'
                : archivedFrom || archivedTo
                    ? 'custom'
                    : 'all';

    const [rows, setRows] = useState<ExerciseArchiveListItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dateFilterMode, setDateFilterMode] = useState<DateFilterMode>(initialDateMode);
    const [customFromDate, setCustomFromDate] = useState(archivedFrom);
    const [customToDate, setCustomToDate] = useState(archivedTo);
    const [selectedArchives, setSelectedArchives] = useState<SelectedArchiveItem[]>([]);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkUiError, setBulkUiError] = useState<string | null>(null);

    const setPage = useCallback(
        (p: number) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (p <= 1) {
                    next.delete('page');
                } else {
                    next.set('page', String(p));
                }
                return next;
            });
        },
        [setSearchParams],
    );

    const selectedArchiveUuids = selectedArchives.map((a) => a.uuid);
    const selectedArchiveSet = new Set(selectedArchiveUuids);
    const isAllCurrentPageSelected = rows.length > 0 && rows.every((r) => selectedArchiveSet.has(r.uuid));
    const selectedScenarioIds = Array.from(
        new Set(selectedArchives.map((a) => (a.scenario_id || '').trim()).filter(Boolean)),
    );
    const hasScenarioMismatch = selectedScenarioIds.length > 1;

    const setPageSize = useCallback(
        (size: number) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('page');
                if (size === DEFAULT_ARCHIVE_PAGE_SIZE) {
                    next.delete('page_size');
                } else {
                    next.set('page_size', String(size));
                }
                return next;
            });
        },
        [setSearchParams],
    );

    const applyDateFilter = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('page');
            next.delete('date_filter');
            next.delete('archived_from');
            next.delete('archived_to');

            if (dateFilterMode === 'today') {
                next.set('date_filter', 'today');
            } else if (dateFilterMode === 'yesterday') {
                next.set('date_filter', 'yesterday');
            } else if (dateFilterMode === 'custom') {
                if (customFromDate) next.set('archived_from', customFromDate);
                if (customToDate) next.set('archived_to', customToDate);
            }
            return next;
        });
    }, [customFromDate, customToDate, dateFilterMode, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = (await v2Api.dashboard.exerciseArchives.listAll({
                page: pageFromUrl,
                page_size: pageSize,
                date_filter:
                    dateFilter === 'today' || dateFilter === 'yesterday'
                        ? (dateFilter as 'today' | 'yesterday')
                        : undefined,
                archived_from:
                    dateFilter === 'today' || dateFilter === 'yesterday'
                        ? undefined
                        : archivedFrom || undefined,
                archived_to:
                    dateFilter === 'today' || dateFilter === 'yesterday'
                        ? undefined
                        : archivedTo || undefined,
            })) as ExerciseArchiveListResponse | null;
            const list = res?.results ?? [];
            const count = res?.count ?? 0;
            const pages = Math.max(1, res?.total_pages ?? 1);
            const cp = res?.current_page ?? pageFromUrl;

            if (pageFromUrl > pages && pages >= 1 && count > 0) {
                setPage(pages);
                return;
            }

            setRows(list);
            setTotalCount(count);
            setTotalPages(pages);
            setCurrentPage(cp);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load reports');
            setRows([]);
            setTotalCount(0);
            setTotalPages(1);
            setCurrentPage(1);
        } finally {
            setLoading(false);
        }
    }, [archivedFrom, archivedTo, dateFilter, pageFromUrl, pageSize, setPage]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        const mode: DateFilterMode =
            dateFilter === 'today'
                ? 'today'
                : dateFilter === 'yesterday'
                    ? 'yesterday'
                    : archivedFrom || archivedTo
                        ? 'custom'
                        : 'all';
        setDateFilterMode(mode);
        setCustomFromDate(archivedFrom);
        setCustomToDate(archivedTo);
    }, [archivedFrom, archivedTo, dateFilter]);

    const rangeStart = totalCount === 0 || rows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const rangeEnd = totalCount === 0 || rows.length === 0 ? 0 : rangeStart + rows.length - 1;

    const toggleRowSelection = (row: ExerciseArchiveListItem) => {
        setSelectedArchives((prev) => {
            const exists = prev.some((a) => a.uuid === row.uuid);
            if (exists) return prev.filter((a) => a.uuid !== row.uuid);
            return [...prev, {uuid: row.uuid, scenario_id: row.scenario_id || ''}];
        });
    };

    const toggleCurrentPageSelection = (checked: boolean) => {
        const pageIds = rows.map((r) => r.uuid);
        setSelectedArchives((prev) => {
            if (checked) {
                const map = new Map(prev.map((a) => [a.uuid, a] as const));
                for (const r of rows) {
                    map.set(r.uuid, {uuid: r.uuid, scenario_id: r.scenario_id || ''});
                }
                return Array.from(map.values());
            }
            return prev.filter((a) => !pageIds.includes(a.uuid));
        });
    };

    const clearSelection = () => setSelectedArchives([]);

    return (
        <div className="mx-auto  space-y-8 px-1 sm:px-0">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm">
                            <FileBarChart className="h-5 w-5" aria-hidden/>
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Report history</h1>
                            <p className="text-sm text-muted-foreground">
                                Saved reports from completed exercise runs. Open a row to view the full report snapshot.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {!loading && !error && (
                        <Badge variant="secondary" className="h-8 px-3 text-xs font-medium tabular-nums">
                            {totalCount} {totalCount === 1 ? 'report' : 'reports'}
                        </Badge>
                    )}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => void load()}
                        disabled={loading}
                    >
                        <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')}/>
                        Refresh
                    </Button>
                    {!loading && !error && (
                        <BulkArchiveReportExport
                            selectedArchives={selectedArchives}
                            onClearSelection={clearSelection}
                            onBusyChange={setBulkLoading}
                    onErrorChange={setBulkUiError}
                    showMessages={false}
                        />
                    )}
                    <Button variant="default" size="sm" className="gap-2 shadow-sm" asChild>
                        <Link to="/scenarios">
                            Scenarios
                            <ArrowRight className="h-3.5 w-3.5"/>
                        </Link>
                    </Button>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <div className="flex flex-col gap-4 border-b border-border bg-muted/25 px-5 py-4 dark:bg-muted/15 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-semibold text-foreground">Past reports</h2>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            A new entry is added to your report history when an exercise run completes. Viewing a report does
                            not change live scenarios.
                        </p>
                    </div>
                    {!loading && !error && (
                        <div className="flex shrink-0 flex-wrap items-center gap-2 text-sm text-muted-foreground sm:pt-0.5">
                            <label htmlFor="archive-date-filter" className="whitespace-nowrap">
                                Date filter
                            </label>
                            <Select
                                id="archive-date-filter"
                                className="h-9 w-[150px] shrink-0"
                                value={dateFilterMode}
                                onChange={(e) => {
                                    const mode = e.target.value as DateFilterMode;
                                    setDateFilterMode(mode);
                                    if (mode !== 'custom') {
                                        setSearchParams((prev) => {
                                            const next = new URLSearchParams(prev);
                                            next.delete('page');
                                            next.delete('date_filter');
                                            next.delete('archived_from');
                                            next.delete('archived_to');
                                            if (mode === 'today' || mode === 'yesterday') {
                                                next.set('date_filter', mode);
                                            }
                                            return next;
                                        });
                                    }
                                }}
                                aria-label="Date filter type"
                            >
                                <option value="all">All dates</option>
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                                <option value="custom">Custom range</option>
                            </Select>
                            {dateFilterMode === 'custom' && (
                                <>
                                    <label htmlFor="archive-archived-from" className="whitespace-nowrap">
                                        From
                                    </label>
                                    <input
                                        id="archive-archived-from"
                                        type="date"
                                        value={customFromDate}
                                        onChange={(e) => setCustomFromDate(e.target.value)}
                                        className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
                                        aria-label="Archived from date"
                                    />
                                    <label htmlFor="archive-archived-to" className="whitespace-nowrap">
                                        To
                                    </label>
                                    <input
                                        id="archive-archived-to"
                                        type="date"
                                        value={customToDate}
                                        onChange={(e) => setCustomToDate(e.target.value)}
                                        className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
                                        aria-label="Archived to date"
                                    />
                                </>
                            )}
                            {dateFilterMode === 'custom' && (
                                <Button type="button" size="sm" onClick={applyDateFilter}>
                                    Apply
                                </Button>
                            )}
                            <label htmlFor="archive-page-size" className="whitespace-nowrap">
                                Rows per page
                            </label>
                            <Select
                                id="archive-page-size"
                                className="h-9 w-[88px] shrink-0"
                                value={String(pageSize)}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                aria-label="Rows per page"
                            >
                                {ARCHIVE_PAGE_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    )}
                    {!loading && !error && selectedArchives.length > 0 && (
                        <div className="sm:basis-full">
                            <Alert className={cn('border-border/60', hasScenarioMismatch && 'border-destructive/40')}>
                                <AlertCircle className={cn('h-4 w-4', hasScenarioMismatch ? 'text-destructive' : 'text-primary')}/>
                                <AlertTitle>
                                    {hasScenarioMismatch
                                        ? 'Mixed scenario selection'
                                        : `Selected ${selectedArchives.length} archive${selectedArchives.length === 1 ? '' : 's'}`}
                                </AlertTitle>
                                <AlertDescription>
                                    {hasScenarioMismatch
                                        ? `You selected ${selectedArchives.length} archives from ${selectedScenarioIds.length} different scenarios.`
                                        : `Scenario ID: ${selectedScenarioIds[0] || '—'}. You can now generate the bulk report.`}
                                </AlertDescription>
                                {hasScenarioMismatch && (
                                    <div className="mt-2 text-xs text-muted-foreground">
                                        Keep one scenario only, then export.
                                    </div>
                                )}
                            </Alert>
                        </div>
                    )}
                    {!loading && !error && bulkUiError && (
                        <div className="sm:basis-full">
                            <Alert variant="destructive" className="border-destructive/40">
                                <AlertCircle className="h-4 w-4"/>
                                <AlertTitle>Bulk report error</AlertTitle>
                                <AlertDescription>{bulkUiError}</AlertDescription>
                            </Alert>
                        </div>
                    )}
                </div>

                <div className="p-0">
                    {loading && (
                        <div className="space-y-0 p-5">
                            <div className="mb-4 flex gap-4">
                                <Skeleton className="h-10 flex-1 rounded-md"/>
                                <Skeleton className="h-10 w-24 rounded-md"/>
                                <Skeleton className="h-10 w-28 rounded-md hidden sm:block"/>
                            </div>
                            {Array.from({length: 6}).map((_, i) => (
                                <Skeleton key={i} className="mb-2 h-12 w-full rounded-md"/>
                            ))}
                        </div>
                    )}

                    {!loading && error && (
                        <div className="p-5">
                            <Alert variant="destructive" className="border-destructive/40">
                                <AlertCircle className="h-4 w-4"/>
                                <AlertTitle>Could not load reports</AlertTitle>
                                <AlertDescription className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <span className="text-destructive/90">{error}</span>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="w-full shrink-0 border-destructive/40 bg-background sm:w-auto"
                                        onClick={() => void load()}
                                    >
                                        Try again
                                    </Button>
                                </AlertDescription>
                            </Alert>
                        </div>
                    )}

                    {!loading && !error && rows.length === 0 && (
                        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                                <Archive className="h-7 w-7 text-muted-foreground" aria-hidden/>
                            </div>
                            <h3 className="text-base font-semibold text-foreground">No report history yet</h3>
                            <p className="mt-2 max-w-md text-sm text-muted-foreground">
                                When you complete an exercise run, a report is saved here. Start or finish a run from an active
                                scenario to build your history.
                            </p>
                            <Button className="mt-6 gap-2" asChild>
                                <Link to="/scenarios">
                                    Go to scenarios
                                    <ArrowRight className="h-4 w-4"/>
                                </Link>
                            </Button>
                        </div>
                    )}

                    {!loading && !error && rows.length > 0 && (
                        <>
                        <Table className="min-w-[720px]">
                            <TableHeader className="sticky top-0 z-[1] border-b border-border/80 bg-muted/50 backdrop-blur-sm [&_tr]:border-b-0">
                                <TableRow className="border-0 hover:bg-transparent">
                                    <TableHead className="h-11 w-[52px] px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                        <input
                                            type="checkbox"
                                            checked={isAllCurrentPageSelected}
                                            onChange={(e) => toggleCurrentPageSelection(e.target.checked)}
                                            disabled={bulkLoading}
                                            aria-label="Select all rows on current page"
                                        />
                                    </TableHead>
                                    <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                        Run name
                                    </TableHead>
                                    <TableHead className="hidden h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground lg:table-cell">
                                        Run period
                                    </TableHead>
                                    <TableHead className="h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                        Saved
                                    </TableHead>
                                    <TableHead className="hidden h-11 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:table-cell">
                                        Scenario ID
                                    </TableHead>
                                    <TableHead className="h-11 px-4 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                        Report
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r, rowIdx) => (
                                    <TableRow
                                        key={r.uuid}
                                        className={cn(
                                            'border-b border-border/50 transition-colors',
                                            rowIdx % 2 === 1 ? 'bg-muted/15' : 'bg-card',
                                            'hover:bg-primary/[0.04] dark:hover:bg-primary/10'
                                        )}
                                    >
                                        <TableCell className="px-4 py-3 align-top">
                                            <input
                                                type="checkbox"
                                                checked={selectedArchiveSet.has(r.uuid)}
                                                onChange={() => toggleRowSelection(r)}
                                                disabled={bulkLoading}
                                                aria-label={`Select report ${r.name}`}
                                            />
                                        </TableCell>
                                        <TableCell className="max-w-[220px] px-4 py-3 align-top">
                                            <p className="truncate font-medium text-foreground">{r.name}</p>
                                            <p className="mt-1 truncate font-mono text-[11px] text-muted-foreground lg:hidden">
                                                {formatRunPeriod(r.start_at, r.end_at)}
                                            </p>
                                        </TableCell>
                                        <TableCell className="hidden px-4 py-3 align-top text-xs text-muted-foreground lg:table-cell">
                      <span className="whitespace-pre-line leading-relaxed">
                        {formatRunPeriod(r.start_at, r.end_at)}
                      </span>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap px-4 py-3 align-top text-xs tabular-nums text-muted-foreground">
                                            {r.archived_at ? format(new Date(r.archived_at), 'PPp') : '—'}
                                        </TableCell>
                                        <TableCell className="hidden max-w-[180px] px-4 py-3 align-top md:table-cell">
                                            <code className="block truncate rounded-md bg-muted/80 px-2 py-1 font-mono text-[11px] text-muted-foreground">
                                                {r.scenario_id || '—'}
                                            </code>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-right align-top">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="gap-1.5 border-border/80 bg-background shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
                                                asChild
                                            >
                                                <Link to={`/scenarios/exercise-archive/${r.uuid}/report`}>
                                                    Open report
                                                    <ExternalLink className="h-3.5 w-3.5 opacity-70"/>
                                                </Link>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {totalPages > 1 && (
                            <div className="border-t border-border bg-card px-2 py-2 sm:px-4">
                                <Pagination
                                    totalPages={totalPages}
                                    currentPage={currentPage}
                                    onFirst={() => setPage(1)}
                                    onLast={() => setPage(totalPages)}
                                    onPrev={() => setPage(currentPage - 1)}
                                    onNext={() => setPage(currentPage + 1)}
                                />
                            </div>
                        )}
                        </>
                    )}
                </div>

                {!loading && !error && rows.length > 0 && (
                    <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/20 px-5 py-3 text-xs text-muted-foreground dark:bg-muted/10">
            <span>
              Showing{' '}
                <span className="font-medium text-foreground tabular-nums">{rangeStart}</span>
                –
                <span className="font-medium text-foreground tabular-nums">{rangeEnd}</span>
                {' '}of{' '}
                <span className="font-medium text-foreground tabular-nums">{totalCount}</span> saved
                {totalCount === 1 ? ' report' : ' reports'} in history
            </span>
                        <span className="hidden sm:inline">Report history is read-only.</span>
                    </div>
                )}
            </div>
        </div>
    );
}
